import React, { useEffect } from "react";
import './App.css';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Suspense } from 'react';

import './vendor/bootstrap-5.3.0-alpha1-dist/css/bootstrap.min.css';
import './vendor/select2@4.1.0-rc.0/select2.min.css'
import './assets/css/style.css';
import './Skeleton/skeleton.css';
import { AppBootstrapSkeleton, PageRouteSkeleton } from "./Skeleton";
import { setAccessToken, setAppReady, setBusinessData, setCartToken, setUser, setutilitiesData } from "./Redux/UserSlice";
import { useDispatch, useSelector } from "react-redux";
import store from "./Redux/Store";
import ApiService from "./Service/ApiService";
import AuthRoutes from "./Authentication/AuthRoutes";
import WithoutAuth from "./Authentication/WithoutAuth";
import { setFavicon } from "./setFavIcon";
import Loader from "./Helper/Loader";

const Login = React.lazy(() => import('./Login/Login'))
const Signup = React.lazy(() => import('./Login/Signup'))
const ForgotPassword = React.lazy(() => import('./Login/ForgotPassword'))
const ResetPassword = React.lazy(() => import('./Login/ResetPassword'))
const CheckInbox = React.lazy(() => import('./Login/CheckInbox'))

const Home = React.lazy(() => import('./Home/Home'))
const MyAccount = React.lazy(() => import('./MyAccount/MyAccount'))
// const OrderDetail = React.lazy(() => import('./MyAccount/OrderDetail'))
const HelpSupport = React.lazy(() => import('./MyAccount/HelpSupport'))

const WishList = React.lazy(() => import('./WishList/WishList'))
const WishListReviewRating = React.lazy(() => import('./WishList/ReviewRating'))

const MyProfile = React.lazy(() => import('./MyAccount/MyProfile'))
const Address = React.lazy(() => import('./MyAccount/Address'))
const AddNewAddress = React.lazy(() => import('./MyAccount/AddNewAddress'))

const Cart = React.lazy(() => import('./Cart/Home'))
const DeliveryAddress = React.lazy(() => import('./Cart/DeliveryAddress'))
const OrderSummary = React.lazy(() => import('./Cart/OrderSummery'))
const PaymentSuccess = React.lazy(() => import('./Cart/PaymentSuccess'))

const ProductList = React.lazy(() => import('./Product/ProductList'))
const ProductDetail = React.lazy(() => import('./Product/ProductDetail'))

const CmsPage = React.lazy(() => import('./Common/CmsPage'))

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [pathname]);

  return null;
}

function AppRoutes() {
  return (
    <BrowserRouter basename="/genesis_unisol/ecommerce_portal">
      <ScrollToTop />
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route path="/cms-page/:slug" element={<CmsPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/check-inbox" element={<CheckInbox />} />

          <Route path="/" element={<Home />} />
          <Route path="/product-list/:id" element={<ProductList />} />
          <Route path="/product-detail/:id" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />

          <Route element={<WithoutAuth />}>
            <Route path="/login" element={<Login />} />
            <Route path="/sign-up" element={<Signup />} />
          </Route>

          <Route element={<AuthRoutes />}>
            <Route path="/my-account" element={<MyAccount />} />
            <Route path="/address" element={<Address />} />
            <Route path="/add-address" element={<AddNewAddress />} />
            <Route path="/edit-address/:id" element={<AddNewAddress />} />
            <Route path="/help-support" element={<HelpSupport />} />
            <Route path="/wish-list" element={<WishList />} />
            <Route path="/wish-list-review-rating" element={<WishListReviewRating />} />
            <Route path="/my-profile" element={<MyProfile />} />
            <Route path="/delivery-address" element={<DeliveryAddress />} />
            <Route path="/order-summary" element={<OrderSummary />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

function App() {
  const dispatch = useDispatch();
  const appReady = useSelector((state) => state?.userData?.appReady);
  const access_token = useSelector((state) => state?.userData?.access_token);

  const urlParams = new URLSearchParams(window.location.search);
  const _token = urlParams.get("token");
  const portalIdFromUrl = urlParams.get("portal_id");

  const getResolvedPortalId = () => {
    const storedPortalId = localStorage.getItem("portal_id");
    if (portalIdFromUrl) {
      if (storedPortalId !== portalIdFromUrl) {
        localStorage.setItem("portal_id", portalIdFromUrl);
      }
      return portalIdFromUrl;
    }
    return storedPortalId || "";
  };

  useEffect(() => {
    if (_token) {
      dispatch(setAccessToken(_token));
    }
  }, [_token, dispatch]);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      try {
        const portal_id = getResolvedPortalId();
        // FOR DYNAMICN PORTAL ID BUSINESS TOKEN
        // const businessTokenUrl = portal_id
        //   ? `get-business-token?portal_id=${encodeURIComponent(portal_id)}`
        //   : ApiService.config.getBusinessTokenApi;

        // FOR STATIC PORTAL ID BUSINESS TOKEN uncomment
        const businessTokenUrl = ApiService.config.getBusinessTokenApi;
        const businessRes = await ApiService.request("post", businessTokenUrl, {});
        if (!businessRes?.status || cancelled) {
          return;
        }

        const businessData = businessRes.data;
        const business_token = businessData?.business_token;
        dispatch(setBusinessData(businessData));

        const { cartToken } = store.getState().userData;
        if (!cartToken && business_token) {
          const cartRes = await ApiService.request("post", "getCartToken", { business_token });
          if (cartRes?.status && !cancelled) {
            dispatch(setCartToken(cartRes.data?.cart_token));
          }
        }

        if (business_token) {
          const utilRes = await ApiService.request("post", "getUtilities", { business_token });
          if (utilRes?.status && !cancelled) {
            dispatch(setutilitiesData(utilRes.data));
            setFavicon(utilRes.data?.ecommerce_fav_icon);
          }
        }

        if (!cancelled) {
          dispatch(setAppReady(true));
        }
      } catch {
        // ApiService already toasts errors
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    if (!access_token) {
      return;
    }

    const loadProfile = async () => {
      try {
        const data = await ApiService.request("get", "me", {});
        if (data?.status) {
          dispatch(setUser(data.data));
        }
      } catch {
        dispatch(setUser({}));
        dispatch(setAccessToken(""));
      }
    };

    loadProfile();
  }, [access_token, dispatch]);

  if (!appReady) {
    return (
      <>
        <ToastContainer />
        <Loader />
      </>
    );
  }

  return (
    <>
      <ToastContainer />
      <AppRoutes />
    </>
  );
}

export default App;
